import { logInfo, logError } from '@/lib/logger'
import type { AppId } from '@/data/apps'
import { APP_ID_ALIASES, RETIRED_APP_IDS } from '@/data/apps'

/**
 * Deep Link Service
 * Handles URL-based deep linking to apps with parameters
 * Supports multiple windows in a single URL for session restore
 *
 * URL Format: #/w=app1:param1=value1;app2:param2=value2
 */

export interface DeepLinkParams {
  [key: string]: string | number | boolean | undefined
}

export interface ParsedWindow {
  appId: AppId
  params: DeepLinkParams
  subtitle?: string
}

export interface DeepLinkIntent {
  appId: AppId
  params: DeepLinkParams
  subtitle?: string
}

/**
 * Parameter aliases for shorter URLs
 * Maps long parameter names to short aliases
 */
const PARAM_ALIASES: Record<string, string> = {
  // Short -> Long
  'ka': 'knowledgeArea',
  'f': 'fileId',
  'd': 'documentId',
  'fn': 'fileName',
  't': 'tab',
  'c': 'category',
  's': 'sessionId',
  'st': 'subtitle',
  'p': 'path',
  'q': 'query',
  'm': 'mode',
  'type': 'type'
}

/**
 * Reverse mapping: Long -> Short
 */
const PARAM_ALIASES_REVERSE: Record<string, string> = Object.fromEntries(
  Object.entries(PARAM_ALIASES).map(([short, long]) => [long, short])
)

export function resolveDeepLinkParamKey(key: string): string {
  return PARAM_ALIASES[key] || key
}

/** An old app id (`admin`, `pdf-viewer`, …) opens the app it became; the aliases live in the app manifest. */
export function resolveDeepLinkAppId(appId: string): string {
  return APP_ID_ALIASES[appId] || appId
}

class DeepLinkService {
  private readonly HASH_PREFIX = '#/w='
  private readonly LEGACY_HASH_PREFIX = '#/windows='

  /**
   * Parse URL hash to extract window configurations
   * Supports both new (#/w=) and legacy (#/windows=) formats
   * Format: #/w=app1:param1=value1,param2=value2;app2:param1=value1
   */
  parseUrl(url?: string): ParsedWindow[] {
    try {
      const hash = url || window.location.hash

      // Check for new or legacy format
      let windowsString = ''
      if (hash.startsWith(this.HASH_PREFIX)) {
        windowsString = hash.substring(this.HASH_PREFIX.length)
      } else if (hash.startsWith(this.LEGACY_HASH_PREFIX)) {
        windowsString = hash.substring(this.LEGACY_HASH_PREFIX.length)
      } else {
        return []
      }

      if (!windowsString) {
        return []
      }

      // Split by ; to get individual window configs
      const windowConfigs = windowsString.split(';').filter(Boolean)

      return windowConfigs.map((config) => {
        // Split by : to separate appId from params
        const [appId, paramsString] = config.split(':')

        if (!appId) {
          throw new Error('Invalid window config: missing appId')
        }

        const params: DeepLinkParams = {}
        let subtitle: string | undefined

        // Parse params (format: key=value,key2=value2)
        if (paramsString) {
          const paramPairs = paramsString.split(',').filter(Boolean)

          for (const pair of paramPairs) {
            const [key, value] = pair.split('=')
            if (key && value !== undefined) {
              const decodedValue = decodeURIComponent(value)

              // Resolve alias to full parameter name
              const fullKey = PARAM_ALIASES[key] || key

              // Special handling for subtitle
              if (fullKey === 'subtitle') {
                subtitle = decodedValue
              } else {
                // Try to parse as number or boolean
                if (decodedValue === 'true') {
                  params[fullKey] = true
                } else if (decodedValue === 'false') {
                  params[fullKey] = false
                } else if (!isNaN(Number(decodedValue)) && decodedValue !== '') {
                  params[fullKey] = Number(decodedValue)
                } else {
                  params[fullKey] = decodedValue
                }
              }
            }
          }
        }

        return {
          appId: resolveDeepLinkAppId(appId) as AppId,
          params,
          subtitle
        }
      })
      .filter((w) => !RETIRED_APP_IDS.has(w.appId))
    } catch (error) {
      logError(error as Error, { context: 'DeepLinkService.parseUrl' })
      return []
    }
  }

  /**
   * Generate URL hash from window configurations
   * Uses short parameter aliases and URL encoding to preserve all characters
   * Note: Subtitle is NOT included in URL as it's a dynamic display detail
   */
  generateUrl(windows: DeepLinkIntent[]): string {
    try {
      if (windows.length === 0) {
        return '#/'
      }

      const windowStrings = windows.map((window) => {
        const { appId, params } = window

        const paramPairs: string[] = []

        // Subtitle is intentionally NOT included in URL
        // Apps should set their own subtitles based on their internal state

        // Add other params with short aliases
        Object.entries(params).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            // Skip non-serializable or non-critical values
            if (key === 'fileUrl' && typeof value === 'string' && value.startsWith('blob:')) {
              return // Don't include Blob URLs in deep links
            }
            if (key === 'fileSize') {
              return // File size is not needed for deep links
            }

            // Use short alias if available, otherwise use full key
            const shortKey = PARAM_ALIASES_REVERSE[key] || key

            // Always encode values properly to preserve case, umlauts, and special chars
            // Don't slug - this can break case-sensitive values like Knowledge Area names
            const encodedValue = encodeURIComponent(String(value))

            paramPairs.push(`${shortKey}=${encodedValue}`)
          }
        })

        if (paramPairs.length === 0) {
          return appId
        }

        return `${appId}:${paramPairs.join(',')}`
      })

      return `${this.HASH_PREFIX}${windowStrings.join(';')}`
    } catch (error) {
      logError(error as Error, { context: 'DeepLinkService.generateUrl' })
      return '#/'
    }
  }

  /**
   * Update browser URL with current window state
   * Uses replaceState to avoid creating browser history entries for every window change
   */
  updateBrowserUrl(windows: DeepLinkIntent[]): void {
    try {
      const newUrl = this.generateUrl(windows)

      // Only update if different from current
      if (window.location.hash !== newUrl) {
        window.history.replaceState(null, '', newUrl)

        logInfo({
          context: 'DeepLinkService',
          url: newUrl,
          windowCount: windows.length
        }, 'Browser URL updated')
      }
    } catch (error) {
      logError(error as Error, { context: 'DeepLinkService.updateBrowserUrl' })
    }
  }

  /**
   * Write the open windows into the address. The subtitle is left out: apps
   * set it from their params.
   */
  updateBrowserUrlFromWindows(windows: { appId: string; params?: DeepLinkParams }[]): void {
    this.updateBrowserUrl(
      windows.map((w) => ({ appId: w.appId as AppId, params: w.params || {}, subtitle: undefined })),
    )
  }

  /**
   * Check if current URL contains deep link
   * Supports both new and legacy formats
   */
  hasDeepLink(): boolean {
    return window.location.hash.startsWith(this.HASH_PREFIX) ||
           window.location.hash.startsWith(this.LEGACY_HASH_PREFIX)
  }

  /**
   * Clear deep link from URL
   */
  clearUrl(): void {
    window.history.replaceState(null, '', '#/')
  }

  /**
   * Get shareable URL for current session
   */
  getShareableUrl(windows: DeepLinkIntent[]): string {
    const hash = this.generateUrl(windows)
    return `${window.location.origin}${window.location.pathname}${hash}`
  }
}

export const deepLinkService = new DeepLinkService()
